/**
 * Google Tag Manager SPA helpers.
 * Bootstrap (container load) is in public/index.html — init only, no page view there.
 */

import {
  buildMarketingPageViewKey,
  shouldTrackMarketingPageView,
} from './shared/marketingRouteExclusions';

const CONTAINER_ID = process.env.REACT_APP_GTM_CONTAINER_ID;
const ENABLED = process.env.REACT_APP_GTM_ENABLED === 'true';

const DEFAULT_ALLOWED_HOSTS = ['yovotrip.com', 'www.yovotrip.com', 'testing.yovotrip.com'];

const ALLOWED_HOSTS = (
  process.env.REACT_APP_GTM_ALLOWED_HOSTS || DEFAULT_ALLOWED_HOSTS.join(',')
)
  .split(',')
  .map((host) => host.trim())
  .filter(Boolean);

/** @type {string | null} */
let lastTrackedPageKey = null;

/**
 * @returns {boolean}
 */
export function isAllowedGtmHostname() {
  if (typeof window === 'undefined') {
    return false;
  }
  return ALLOWED_HOSTS.includes(window.location.hostname);
}

/**
 * @returns {boolean}
 */
export function isGtmEnabled() {
  return (
    ENABLED &&
    Boolean(CONTAINER_ID) &&
    isAllowedGtmHostname() &&
    typeof window !== 'undefined' &&
    Array.isArray(window.dataLayer)
  );
}

/**
 * Dedupe key for SPA page view tracking (same shape as Meta Pixel).
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 * @returns {string}
 */
export function buildGtmPageViewKey(pathname, search) {
  return buildMarketingPageViewKey(pathname, search);
}

/**
 * @param {string | undefined | null} pathname
 * @returns {boolean}
 */
export function shouldTrackGtmPageView(pathname) {
  return shouldTrackMarketingPageView(pathname);
}

/**
 * Track one virtual page view per distinct pathname + search.
 * Dedupes React StrictMode double-effects in development.
 *
 * Uses `virtual_page_view` to avoid colliding with GA4 Enhanced Measurement's
 * automatic `page_view` event — configure GTM to fire on this custom event.
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 */
export function trackGtmPageView(pathname, search) {
  if (!isGtmEnabled()) {
    return;
  }

  if (!shouldTrackGtmPageView(pathname)) {
    return;
  }

  const pageKey = buildGtmPageViewKey(pathname, search);

  if (lastTrackedPageKey === pageKey) {
    return;
  }

  lastTrackedPageKey = pageKey;
  window.dataLayer.push({
    event: 'virtual_page_view',
    page_path: pageKey,
    page_title: document.title,
    page_location: window.location.href,
  });
}

/** @type {Set<string>} */
const trackedGtmEventKeys = new Set();

/**
 * Push a custom GTM/GA4 event to the dataLayer.
 * Use for validation-gated or lifecycle events (not simple DOM clicks).
 *
 * @param {string} eventName - GA4 event name (e.g. `hotel_results_viewed`).
 * @param {Object.<string, string | number | boolean>} [payload={}] - Extra event parameters.
 * @param {string} [dedupeKey] - When set, fires at most once per key per page session.
 */
export function trackGtmEvent(eventName, payload = {}, dedupeKey = null) {
  if (!isGtmEnabled()) {
    return;
  }

  if (dedupeKey) {
    const key = `${eventName}:${dedupeKey}`;
    if (trackedGtmEventKeys.has(key)) {
      return;
    }
    trackedGtmEventKeys.add(key);
  }

  window.dataLayer.push({
    event: eventName,
    ...payload,
  });
}
